import type { StateCreator } from 'zustand';
import { type AppStore } from '../store';
import { type CoveragePremium, type Premium } from 'dto/CoveragePremium';
import { CoverageProduct } from 'dto/marketableProduct';
import {
  type CoverageSelection,
  getInitialCoverageSelections,
  createEmptyCoverageSelections,
  filterAvailableCoverages,
} from 'state/utils/coverage-init';
import { getInitialState, getCoverageByCoverageProduct } from 'utils';
import {
  isPremiumSelectable,
  getSuggestedOrMinSumInsuredPremium,
} from 'utils/premium-utils';

// Re-export for backwards compatibility
export { type CoverageSelection, getInitialCoverageSelections };

/**
 * Get initial state for Coverage slice
 * @returns Initial coverage state with computed selections based on server data
 */
export const getInitialCoverageState = () => {
  const { marketableProduct, calculatorInput } = getInitialState();

  const { coverageSelections, selectedCoverages } =
    getInitialCoverageSelections(
      marketableProduct?.coverages ?? [],
      calculatorInput
    );

  // Only set userHasSeenCoverages true if both marketableProduct and calculatorInput have valid coverages
  const userHasSeenCoverages = !!(
    marketableProduct?.coverages &&
    marketableProduct.coverages.length > 0 &&
    calculatorInput?.coverages &&
    Object.keys(calculatorInput.coverages).length > 0
  );

  return {
    coveragePremiums: undefined as CoveragePremium[] | undefined,
    coverageSelections,
    selectedCoverages,
    lifeInsuranceFallbackPremiumAssigned: false,
    userHasSeenCoverages,
  };
};

export interface CoverageSlice {
  // State
  coveragePremiums: CoveragePremium[] | undefined;
  coverageSelections: Record<CoverageProduct, CoverageSelection | undefined>;
  selectedCoverages: CoverageProduct[];
  lifeInsuranceFallbackPremiumAssigned: boolean;
  userHasSeenCoverages: boolean;

  // Actions
  setCoveragePremiums: (premiums: CoveragePremium[]) => void;
  setSelectedCoverages: (
    coverages:
      | CoverageProduct[]
      | ((prev: CoverageProduct[]) => CoverageProduct[])
  ) => void;
  selectCoverage: (coverageProduct: CoverageProduct) => void;
  removeCoverageSelection: (coverageProduct: CoverageProduct) => void;
  toggleSelectedCoverages: (coverageProduct: CoverageProduct) => void;
  handleSumInsuredChange: (
    coverageProduct: CoverageProduct,
    premium: Premium
  ) => void;
  setLifeInsuranceFallbackPremiumAssigned: (value: boolean) => void;
  setUserHasSeenCoverages: (value: boolean) => void;
}

export const createCoverageSlice: StateCreator<
  AppStore,
  [['zustand/devtools', never]],
  [],
  CoverageSlice
> = (set, get) => {
  // Initialize coverage state
  const { marketableProduct } = getInitialState();

  /**
   * Helper function to adjust LIFE insurance selection if needed based on minimum requirements.
   * Returns adjusted selection and whether fallback flag should be set.
   */
  const adjustLifeSelectionIfNeeded = (
    coveragePremiums: CoveragePremium[],
    currentLifeSelection: CoverageSelection | undefined,
    selectedCoveragesArray: CoverageProduct[],
    lifeCoverage: any
  ): {
    adjustedSelection: CoverageSelection | undefined;
    shouldSetFallbackFlag: boolean;
  } => {
    if (
      !currentLifeSelection ||
      !lifeCoverage?.minSumInsuredWhenOptionalCoverageSelected
    ) {
      return {
        adjustedSelection: currentLifeSelection,
        shouldSetFallbackFlag: false,
      };
    }

    // Count how many optional coverages are selected (excluding LIFE)
    const otherCoveragesSelected = selectedCoveragesArray.filter(
      (c) => c !== CoverageProduct.LIFE
    ).length;

    // Determine the correct minimum
    const effectiveMinimum =
      otherCoveragesSelected > 0
        ? lifeCoverage.minSumInsuredWhenOptionalCoverageSelected
        : lifeCoverage.minSumInsured;

    // If current selection is below the new minimum, adjust it
    if (currentLifeSelection.sumInsured < effectiveMinimum) {
      const adjustedPremium = getSuggestedOrMinSumInsuredPremium(
        coveragePremiums,
        CoverageProduct.LIFE,
        selectedCoveragesArray
      );

      if (adjustedPremium) {
        const shouldSetFallbackFlag =
          currentLifeSelection.sumInsured < lifeCoverage.minSumInsured &&
          adjustedPremium.sumInsured >= lifeCoverage.minSumInsured;

        return {
          adjustedSelection: {
            sumInsured: adjustedPremium.sumInsured,
            deductibleDays: adjustedPremium.deductibleDays ?? null,
          },
          shouldSetFallbackFlag,
        };
      }
    }

    return {
      adjustedSelection: currentLifeSelection,
      shouldSetFallbackFlag: false,
    };
  };

  return {
    // Initial state
    ...getInitialCoverageState(),

    // Actions
    setCoveragePremiums: (premiums: CoveragePremium[]) => {
      // Reset fallback flag when receiving new premium data (mimics old submitBackgroundInfo pattern)
      set(
        { lifeInsuranceFallbackPremiumAssigned: false },
        false,
        'coverageSlice/setCoveragePremiums/resetFallbackFlag'
      );

      const {
        backgroundInfoForm,
        coverageSelections: currentSelections,
        selectedCoverages: currentSelectedCoverages,
        userHasSeenCoverages: hasSeenCoverages,
      } = get();

      // Filter selectedCoverages by available coverages (based on age/working status)
      const availableCoverages = filterAvailableCoverages(
        marketableProduct?.coverages,
        backgroundInfoForm
      );
      const availableCoverageProducts =
        availableCoverages?.map((c) => c.coverageProduct) ?? [];
      const effectiveSelectedCoverages = currentSelectedCoverages.filter(
        (c: CoverageProduct) => availableCoverageProducts.includes(c)
      );

      const includedCoverageProducts = premiums.map((cp) => cp.coverageProduct);

      // Update coverage selections with validation
      const initialUpdatedSelections = createEmptyCoverageSelections();

      const updatedSelections: Record<
        CoverageProduct,
        CoverageSelection | undefined
      > = Object.values(CoverageProduct).reduce<
        Record<CoverageProduct, CoverageSelection | undefined>
      >((acc, coverageProduct) => {
        if (!includedCoverageProducts.includes(coverageProduct)) {
          acc[coverageProduct] = undefined;
          return acc;
        }

        const currentSelection = currentSelections[coverageProduct];

        // Check if current selection is still valid
        // Only validate and preserve if user has already seen coverages
        if (hasSeenCoverages && currentSelection) {
          // Find the matching premium from the coveragePremiums
          const matchingPremium = premiums
            .find((cp) => cp.coverageProduct === coverageProduct)
            ?.premiums.find(
              (p) =>
                p.sumInsured === currentSelection.sumInsured &&
                (p.deductibleDays === currentSelection.deductibleDays ||
                  !currentSelection.deductibleDays)
            );

          const isValid =
            matchingPremium &&
            isPremiumSelectable(
              premiums,
              coverageProduct,
              effectiveSelectedCoverages,
              matchingPremium
            );

          if (isValid) {
            acc[coverageProduct] = currentSelection;
            return acc;
          }
        }

        // Invalid or no current selection - get suggested/minimum premium
        const suggestedPremium = getSuggestedOrMinSumInsuredPremium(
          premiums,
          coverageProduct,
          effectiveSelectedCoverages
        );

        if (!suggestedPremium) {
          acc[coverageProduct] = undefined;
          return acc;
        }

        // Detect life insurance fallback scenario
        if (
          coverageProduct === CoverageProduct.LIFE &&
          currentSelection &&
          suggestedPremium
        ) {
          const lifeCoverage = getCoverageByCoverageProduct(
            marketableProduct?.coverages ?? [],
            CoverageProduct.LIFE
          );

          if (
            lifeCoverage &&
            currentSelection.sumInsured < lifeCoverage.minSumInsured &&
            suggestedPremium.sumInsured >= lifeCoverage.minSumInsured
          ) {
            // Set fallback flag when LIFE sum insured is adjusted upward to meet minSumInsured
            set(
              { lifeInsuranceFallbackPremiumAssigned: true },
              false,
              'coverageSlice/setCoveragePremiums/setFallbackFlag'
            );
          }
        }

        acc[coverageProduct] = {
          sumInsured: suggestedPremium.sumInsured,
          deductibleDays: suggestedPremium.deductibleDays ?? null,
        };

        return acc;
      }, initialUpdatedSelections);

      // Apply LIFE-only minimum validation when user has seen coverages
      if (
        hasSeenCoverages &&
        effectiveSelectedCoverages.length === 1 &&
        effectiveSelectedCoverages[0] === CoverageProduct.LIFE
      ) {
        const lifeCoverage = getCoverageByCoverageProduct(
          marketableProduct?.coverages ?? [],
          CoverageProduct.LIFE
        );

        if (lifeCoverage) {
          const { adjustedSelection, shouldSetFallbackFlag } =
            adjustLifeSelectionIfNeeded(
              premiums,
              updatedSelections[CoverageProduct.LIFE],
              effectiveSelectedCoverages,
              lifeCoverage
            );

          if (adjustedSelection) {
            updatedSelections[CoverageProduct.LIFE] = adjustedSelection;
          }

          if (shouldSetFallbackFlag) {
            set(
              { lifeInsuranceFallbackPremiumAssigned: true },
              false,
              'coverageSlice/setCoveragePremiums/setFallbackFlagLifeOnly'
            );
          }
        }
      }

      set(
        {
          coveragePremiums: premiums,
          coverageSelections: updatedSelections,
        },
        false,
        'coverageSlice/setCoveragePremiums'
      );
    },

    selectCoverage: (coverageProduct: CoverageProduct) => {
      set(
        (state: CoverageSlice) => {
          const updatedSet = new Set(state.selectedCoverages);
          updatedSet.add(coverageProduct);
          return { selectedCoverages: Array.from(updatedSet) };
        },
        false,
        'coverageSlice/selectCoverage'
      );
    },

    setSelectedCoverages: (
      coverages:
        | CoverageProduct[]
        | ((prev: CoverageProduct[]) => CoverageProduct[])
    ) => {
      set(
        (state: CoverageSlice) => ({
          selectedCoverages:
            typeof coverages === 'function'
              ? coverages(state.selectedCoverages)
              : coverages,
        }),
        false,
        'coverageSlice/setSelectedCoverages'
      );
    },

    removeCoverageSelection: (coverageProduct: CoverageProduct) => {
      set(
        (state: CoverageSlice) => {
          const updatedSet = new Set(state.selectedCoverages);
          updatedSet.delete(coverageProduct);
          return { selectedCoverages: Array.from(updatedSet) };
        },
        false,
        'coverageSlice/removeCoverageSelection'
      );
    },

    toggleSelectedCoverages: (coverageProduct: CoverageProduct) => {
      const state = get();
      const prevSelectedCoverages = state.selectedCoverages;
      const coveragePremiums = state.coveragePremiums;
      const prevCoverageSelections = state.coverageSelections;

      if (!coveragePremiums) {
        // No premium data yet, just toggle the array
        set(
          {
            selectedCoverages: prevSelectedCoverages.includes(coverageProduct)
              ? prevSelectedCoverages.filter(
                  (c: CoverageProduct) => c !== coverageProduct
                )
              : [...prevSelectedCoverages, coverageProduct],
          },
          false,
          'coverageSlice/toggleSelectedCoverages/noPremiumData'
        );
        return;
      }

      let newSelectedCoverages: CoverageProduct[];
      const newCoverageSelections = { ...prevCoverageSelections };

      if (prevSelectedCoverages.includes(coverageProduct)) {
        // DESELECTING a coverage
        newSelectedCoverages = prevSelectedCoverages.filter(
          (coverage: CoverageProduct) => coverage !== coverageProduct
        );
      } else {
        // SELECTING a coverage
        newSelectedCoverages = [...prevSelectedCoverages, coverageProduct];
      }

      // Check if LIFE insurance needs adjustment due to minSumInsuredWhenOptionalCoverageSelected
      const lifeCoverage = marketableProduct?.coverages.find(
        (c) => c.coverageProduct === CoverageProduct.LIFE
      );

      if (
        lifeCoverage?.minSumInsuredWhenOptionalCoverageSelected &&
        newSelectedCoverages.includes(CoverageProduct.LIFE)
      ) {
        const currentLifeSelection =
          newCoverageSelections[CoverageProduct.LIFE];

        const { adjustedSelection, shouldSetFallbackFlag } =
          adjustLifeSelectionIfNeeded(
            coveragePremiums,
            currentLifeSelection,
            newSelectedCoverages,
            lifeCoverage
          );

        if (adjustedSelection) {
          newCoverageSelections[CoverageProduct.LIFE] = adjustedSelection;
        }

        if (shouldSetFallbackFlag) {
          set(
            { lifeInsuranceFallbackPremiumAssigned: true },
            false,
            'coverageSlice/toggleSelectedCoverages/setFallbackFlag'
          );
        }
      }

      set(
        {
          selectedCoverages: newSelectedCoverages,
          coverageSelections: newCoverageSelections,
        },
        false,
        'coverageSlice/toggleSelectedCoverages'
      );
    },

    handleSumInsuredChange: (
      coverageProduct: CoverageProduct,
      premium: Premium
    ) => {
      set(
        (state: CoverageSlice) => ({
          coverageSelections: {
            ...state.coverageSelections,
            [coverageProduct]: {
              sumInsured: premium.sumInsured,
              deductibleDays: premium.deductibleDays ?? null,
            },
          },
        }),
        false,
        'coverageSlice/handleSumInsuredChange'
      );
    },

    setLifeInsuranceFallbackPremiumAssigned: (value: boolean) => {
      set(
        { lifeInsuranceFallbackPremiumAssigned: value },
        false,
        'coverageSlice/setLifeInsuranceFallbackPremiumAssigned'
      );
    },

    setUserHasSeenCoverages: (value: boolean) => {
      set(
        { userHasSeenCoverages: value },
        false,
        'coverageSlice/setUserHasSeenCoverages'
      );
    },
  };
};
